import { useState } from 'react';
import { IconCamera, IconClockHour4, IconLock, IconReceipt, IconSend } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { PageHeader } from '@hana/shell';
import { Modal, StateView, useToast, type ScreenState } from '@hana/ui';
import {
  ADV0, ADV_BLOCK, CAP0, CAP_KINDS, CAP_RCVS, CAP_REASONS, CAP_TRIPS, CAP_TYPES, COSTS0, RETURN_REASON, STATUS_LABEL, advDiff, advHold, advStatus,
  capErrors, capToCost, costMeta, costSum, costTone, fmtIn, isBlocked, openAdvances, retake, spentOf, vnd, type CapForm, type Cost,
} from './costData';
import './cost.i18n';
import s from './cost.module.css';

const STATES: ScreenState[] = ['ready', 'loading', 'empty', 'error', 'noperm'];
function forcedState(): ScreenState {
  const v = new URLSearchParams(location.search).get('state');
  return STATES.includes(v as ScreenState) ? (v as ScreenState) : 'ready';
}

type Seg = 'cost' | 'adv' | 'set';
const SEGS: readonly (readonly [Seg, string])[] = [['cost', 'mvc.segCost'], ['adv', 'mvc.segAdv'], ['set', 'mvc.segSet']];

/** Mint-Mov-Phone khung 3 "Chi phí · tạm ứng · quyết toán" (giả lập, giữ trong bộ nhớ, toast có Hoàn tác). Sheet "Khoản chi mới" mở từ nút Chụp biên nhận. */
export function CostScreen() {
  const t = useT();
  const state = forcedState();
  const toast = useToast();
  const [seg, setSeg] = useState<Seg>('cost');
  const [costs, setCosts] = useState<readonly Cost[]>(COSTS0);
  const [settled, setSettled] = useState<Record<string, boolean>>({});
  const list = state === 'empty' ? [] : costs;
  const open = openAdvances(ADV0, settled);
  const blocked = isBlocked(open);
  const money = state !== 'noperm';
  const [cap, setCap] = useState<CapForm | null>(null);
  const [tried, setTried] = useState(false);
  const errs = cap ? capErrors(cap) : [];
  const upd = (o: Partial<CapForm>) => { setCap({ ...(cap ?? CAP0), ...o }); setTried(false); };
  const openCap = () => {
    if (!money) { toast({ msg: t('mvc.noPerm') }); return; }
    setCap(CAP0); setTried(false);
  };
  const sendCap = () => {
    if (!cap) return;
    if (errs.length) { setTried(true); return; }
    const prev = costs;
    const now = new Date();
    const item = capToCost(cap, `x${now.getTime()}`, `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`);
    setCosts([item, ...costs]);
    setCap(null); setTried(false);
    toast({ msg: t('mvc.sent', { k: item.k, amt: vnd(item.amt) }), undo: () => setCosts(prev) });
  };

  const doRetake = (id: string) => {
    const prev = costs;
    setCosts(retake(costs, id));
    toast({ msg: t('mvc.retaken'), undo: () => setCosts(prev) });
  };
  const doSettle = (id: string) => {
    const prev = settled;
    setSettled({ ...settled, [id]: true });
    toast({ msg: t('mvc.settleSent', { id }), undo: () => setSettled(prev) });
  };

  return (
    <>
      <PageHeader title={t('mvc.title')} />
      <div className={s.page}>
        <div role="tablist" aria-label={t('mvc.tabs')} className={s.seg}>
          {SEGS.map(([k, l]) => <button key={k} type="button" role="tab" aria-selected={seg === k} onClick={() => setSeg(k)}>{t(l)}</button>)}
        </div>
        {state === 'error' && <div role="status" className={s.net}><span><b>{t('mvc.offline')}</b> {t('mvc.offlineTx')}</span></div>}
        {state === 'noperm' && <div className={s.lock}><IconLock size={22} stroke={1.9} aria-hidden="true" /><span>{t('mvc.lock')}</span></div>}
        <StateView state={state === 'error' || state === 'noperm' || state === 'empty' ? 'ready' : state} skeleton={<>{[0, 1, 2].map((i) => <div key={i} className={s.sk} aria-hidden="true" />)}</>}>
          {seg === 'cost' && (
            <>
              <section className={s.card} aria-label={t('mvc.today')}>
                <div className={s.line}><span>{t('mvc.today')}</span><b className={s.big}>{money ? vnd(costSum(list)) : '••• ₫'}</b></div>
                <div className={s.meta}>{costMeta(list)}</div>
                <button type="button" className={s.main} onClick={openCap}><IconCamera size={24} stroke={2} aria-hidden="true" />{t('mvc.snap')}</button>
                <div className={s.rules}>
                  <span title={t('mvc.due')} aria-label={t('mvc.due')}><IconClockHour4 size={15} stroke={1.8} aria-hidden="true" />{t('mvc.dueS')}</span>
                  <span title={t('mvc.late')} aria-label={t('mvc.late')}><IconLock size={15} stroke={1.8} aria-hidden="true" />{t('mvc.lateS')}</span>
                </div>
              </section>
              {list.length === 0 && <div className={s.empty}>{t('mvc.none')}</div>}
              {list.map((c) => (
                <section key={c.id} className={`${s.card} ${s.item}`} aria-label={c.k}>
                  <div className={s.line}><b className={s.t}>{c.k}</b><b className={s.t}>{money ? vnd(c.amt) : '••• ₫'}</b></div>
                  <div className={s.sub}>{c.time} · {c.trip} · {c.ncc}</div>
                  <div className={s.chips}><span className={s.chip}>{c.kind}</span><span className={s.chip} data-t={costTone(c.s)}>{STATUS_LABEL[c.s]}</span></div>
                  {c.s === 'ret' && (
                    <>
                      <div className={s.errtx}>{RETURN_REASON}</div>
                      <button type="button" className={s.ghost} onClick={() => doRetake(c.id)}><IconCamera size={16} stroke={1.8} aria-hidden="true" />{t('mvc.retake')}</button>
                    </>
                  )}
                </section>
              ))}
            </>
          )}
          {seg === 'adv' && (
            <>
              <section className={s.card} aria-label={t('mvc.mine')}>
                <div className={s.line}><span>{t('mvc.holding')}</span><b className={s.big}>{money ? vnd(advHold(open)) : '••• ₫'}</b></div>
                {blocked && <div role="alert" className={s.alert}>{ADV_BLOCK}</div>}
                <button type="button" className={s.btn} aria-disabled={blocked} onClick={() => (blocked ? toast({ msg: ADV_BLOCK }) : toast({ msg: t('mvc.reqSent') }))}>
                  <IconReceipt size={16} stroke={1.8} aria-hidden="true" />{t('mvc.req')}
                </button>
              </section>
              {ADV0.map((a) => {
                const st = advStatus(a, !!settled[a.id]);
                return (
                  <section key={a.id} className={`${s.card} ${s.item}`} aria-label={a.id}>
                    <div className={s.line}><b className={s.t}>{a.id}</b><b className={s.t}>{money ? vnd(a.amt) : '••• ₫'}</b></div>
                    <div className={s.sub}>{t('mvc.advSub', { trip: a.trip, date: a.date, due: a.due })}</div>
                    <span className={s.chip} data-t={st.tone} style={{ alignSelf: 'flex-start' }}>{st.label}</span>
                  </section>
                );
              })}
            </>
          )}
          {seg === 'set' && (
            <>
              <div className={s.meta}>{t('mvc.pick')}</div>
              {ADV0.map((a) => {
                const done = !!settled[a.id];
                const diff = advDiff(a);
                const st = done ? { label: t('mvc.stDone'), tone: 'ok' as const } : a.overdue ? { label: t('mvc.stOver'), tone: 'err' as const } : { label: t('mvc.stDue', { d: a.due.slice(0, 6) }), tone: 'pri' as const };
                return (
                  <section key={a.id} className={`${s.card} ${s.item}`} aria-label={t('mvc.setAria', { id: a.id })} style={a.overdue && !done ? { boxShadow: 'var(--sh), 0 0 0 2px var(--err)' } : undefined}>
                    <div className={s.line}><b className={s.t}>{a.id} · {a.trip}</b><span className={s.chip} data-t={st.tone}>{st.label}</span></div>
                    <div className={s.sres} data-k="first"><span>{t('mvc.adv')}</span><b>{money ? vnd(a.amt) : '••• ₫'}</b></div>
                    {a.spent.map(([k, v]) => <div key={k} className={s.sres}><span>{k}</span><b>{money ? vnd(v) : '••• ₫'}</b></div>)}
                    <div className={s.sres} data-k={diff >= 0 ? 'diff' : 'over'}><span>{diff >= 0 ? t('mvc.surplus') : t('mvc.excess')}</span><b>{money ? vnd(Math.abs(diff)) : '••• ₫'}</b></div>
                    <div className={s.hint}>{done ? t('mvc.hChecking') : diff >= 0 ? t('mvc.hSurplus') : t('mvc.hExcess')}</div>
                    {!done && money && spentOf(a) >= 0 && <button type="button" className={s.btn} style={{ background: 'var(--btn)', color: 'var(--btn-tx)' }} onClick={() => doSettle(a.id)}><IconSend size={16} stroke={1.8} aria-hidden="true" />{t('mvc.settle')}</button>}
                  </section>
                );
              })}
            </>
          )}
        </StateView>
      </div>
      <Modal
        open={cap !== null}
        onOpenChange={(o) => !o && setCap(null)}
        title={t('mvc.newTitle')}
        footer={<button type="button" className={s.main} style={{ width: '100%' }} onClick={sendCap}><IconSend size={16} stroke={1.8} aria-hidden="true" />{t('mvc.sendAcc')}</button>}
      >
        {cap && (
          <div className={s.form}>
            <div className={s.photo}>
              <div className={s.doc} aria-hidden="true"><i /><i /><i /><i data-k="amt" /></div>
              <div><b className={s.okx}>{t('mvc.photoOk')}</b>{t('mvc.photoOkTx')}<br />{t('mvc.photoNote')}</div>
            </div>
            <div><div className={s.lbl}>{t('mvc.type')}</div><div className={s.opts}>{CAP_TYPES.map((o) => <button key={o} type="button" aria-pressed={cap.type === o} onClick={() => upd({ type: o })}>{o}</button>)}</div></div>
            <label className={s.fld}><span>{t('mvc.amt')}</span><input inputMode="numeric" value={cap.amt} data-big="1" aria-invalid={tried && !cap.amt.trim() ? true : undefined} onChange={(e) => upd({ amt: fmtIn(e.target.value) })} /></label>
            <label className={s.fld}><span>{t('mvc.seller')}</span><input value={cap.ncc} onChange={(e) => upd({ ncc: e.target.value })} /></label>
            <div><div className={s.lbl}>{t('mvc.tripHint')}</div><div className={s.opts}>{CAP_TRIPS.map((o) => <button key={o} type="button" aria-pressed={cap.trip === o} onClick={() => upd({ trip: o })}>{o}</button>)}</div></div>
            <div><div className={s.lbl}>{t('mvc.docKind')}</div>
              <div role="radiogroup" aria-label={t('mvc.docKind')} className={s.rseg}>{CAP_KINDS.map(([k, l]) => <button key={k} type="button" role="radio" aria-checked={cap.kind === k} onClick={() => upd({ kind: k })}>{l}</button>)}</div>
            </div>
            {cap.kind === 'inv' && (
              <>
                <label className={s.fld}><span>{t('mvc.invMst')}</span><input placeholder={t('mvc.invPh')} value={cap.inv} onChange={(e) => upd({ inv: e.target.value })} /></label>
                <div className={s.sub}>{t('mvc.mstAi')}</div>
                <button type="button" className={s.wait} aria-pressed={cap.wait} onClick={() => upd({ wait: !cap.wait })}>{cap.wait ? t('mvc.waitOn') : t('mvc.waitOff')}</button>
              </>
            )}
            {cap.kind === 'no' && (
              <>
                <div><div className={s.lbl}>{t('mvc.noInvWhy')}</div><div className={s.col}>{CAP_REASONS.map((o) => <button key={o} type="button" aria-pressed={cap.reason === o} onClick={() => upd({ reason: o })}>{o}</button>)}</div></div>
                <div><div className={s.lbl}>{t('mvc.rcvKind')}</div><div role="radiogroup" aria-label={t('mvc.rcvAria')} className={s.rseg}>{CAP_RCVS.map(([k, l]) => <button key={k} type="button" role="radio" aria-checked={cap.rcv === k} onClick={() => upd({ rcv: k })}>{l}</button>)}</div></div>
              </>
            )}
            {tried && errs.length > 0 && <div role="alert" className={s.errmsg}>{errs.join(' · ')}</div>}
          </div>
        )}
      </Modal>
    </>
  );
}
